import { useQuery } from "@tanstack/react-query";
import { format, subMonths } from "date-fns";

import { apiGet, mockFor, mockResponse, queryKey } from "./client";
import { officesFingerprint, officesSettled, officeDataAvailable, isAllOffices, liveOffices, useOfficeMapping, type OfficeMapping } from "./office-mapping";
import { TICKET_BUCKETS, type TicketBucket } from "./types";
import type {
  AgreementCultureSummary,
  AgreementRateByBucketPoint,
  AgreementRatePoint,
  CreditCultureSeries,
  IndustryCode,
  SegmentFilter,
} from "./types";

/** Matches the small-ticket framing on Productivity's Automation Readiness Matrix. */
export const SMALL_TICKET_THRESHOLD_DOLLARS = 7_500;

const PERIOD_COUNT = 18;

export const CULTURE_INDUSTRIES: IndustryCode[] = [
  "Construction",
  "Transportation & Trucking",
  "Manufacturing",
  "Medical & Healthcare",
  "Restaurant & Food Service",
];

/** The industry carrying the deliberate late-period dip. */
export const DRIFTING_INDUSTRY: IndustryCode = "Restaurant & Food Service";

const INDUSTRY_BASE: Record<string, number> = {
  Construction: 84,
  "Transportation & Trucking": 79,
  Manufacturing: 87,
  "Medical & Healthcare": 90,
  "Restaurant & Food Service": 81,
};

/** Segment tilt: PG deals agree slightly less often than corporate-guaranteed ones. */
const SEGMENT_SHIFT: Record<SegmentFilter, number> = { all: 0, PG: -2.4, Corp: 1.8 };

/** Deterministic pseudo-noise so charts stay stable between renders. */
function wobble(seed: number, amplitude: number) {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return (value - Math.floor(value) - 0.5) * 2 * amplitude;
}

function round(value: number, decimals = 1) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function periods() {
  return Array.from({ length: PERIOD_COUNT }, (_, index) =>
    format(subMonths(new Date(), PERIOD_COUNT - 1 - index), "MMM yyyy"),
  );
}

/** Small-ticket climbs toward a high, stable plateau; the rest sits lower and jumpier. */
function buildAgreementRateTrend(labels: string[], segment: SegmentFilter): AgreementRatePoint[] {
  const shift = SEGMENT_SHIFT[segment];

  return labels.map((period, index) => {
    const progress = index / (PERIOD_COUNT - 1);
    // Saturating curve: 72% → ~90%, flattening in the last third.
    const plateau = 72 + 19 * (1 - Math.exp(-3.1 * progress));

    return {
      period,
      agreementRateSmallTicket: round(
        Math.min(93.5, plateau + shift + wobble(index + 1, 0.9)),
      ),
      agreementRateRest: round(66 + shift * 0.6 + wobble(index + 41, 4.6)),
    };
  });
}

/**
 * Smooth gradient across the 7 buckets: the smallest tracks the small-ticket
 * series above, each larger bucket steps down a few points.
 */
const BUCKET_STEP: Record<TicketBucket, number> = {
  "0-10K": 0,
  "10-25K": -4.2,
  "25-50K": -8.1,
  "50-100K": -12.4,
  "100-150K": -16.2,
  "150-200K": -19.8,
  "200-250K": -23.1,
  "Over 250K": -26.4,
};

function buildAgreementRateByBucket(
  trend: AgreementRatePoint[],
): AgreementRateByBucketPoint[] {
  return trend.map((point, index) => ({
    period: point.period,
    byBucket: Object.fromEntries(
      TICKET_BUCKETS.map((bucket, bucketIndex) => [
        bucket,
        round(
          Math.max(
            42,
            point.agreementRateSmallTicket +
              BUCKET_STEP[bucket] +
              wobble(index * 7 + bucketIndex + 3, 1.4),
          ),
        ),
      ]),
    ) as Record<TicketBucket, number>,
  }));
}

/** Four flat industries plus one unmistakable dip across the final three periods. */
function buildCreditCultureTrend(labels: string[]): CreditCultureSeries[] {
  return CULTURE_INDUSTRIES.map((industryCode, seriesIndex) => {
    const base = INDUSTRY_BASE[industryCode] ?? 84;
    const drifting = industryCode === DRIFTING_INDUSTRY;

    return {
      industryCode,
      trend: labels.map((period, index) => {
        const fromEnd = PERIOD_COUNT - 1 - index;
        const dip = drifting && fromEnd <= 2 ? [21, 14, 8][fromEnd]! : 0;
        return {
          period,
          agreementRate: round(base + wobble(index + seriesIndex * 31, 1.5) - dip),
        };
      }),
    };
  });
}

function generateAgreementCultureSummary(
  offices: OfficeMapping[],
  segment: SegmentFilter,
  office?: string,
): AgreementCultureSummary {
  const dataAvailable = officeDataAvailable(offices, office);

  const labels = periods();
  const agreementRateTrend = buildAgreementRateTrend(labels, segment);

  return {
    dataAvailable,
    smallTicketThresholdDollars: SMALL_TICKET_THRESHOLD_DOLLARS,
    // The mock is the only place a genuine agreement rate exists, because it is invented.
    // The live endpoint reports "approval_rate" instead — see CultureMetric in types.ts.
    metric: "agreement_rate",
    metricLabel: "Agreement rate",
    metricNote: "",
    agreementRateTrend: dataAvailable ? agreementRateTrend : [],
    agreementRateByBucket: dataAvailable ? buildAgreementRateByBucket(agreementRateTrend) : [],
    creditCultureTrend: dataAvailable ? buildCreditCultureTrend(labels) : [],
  };
}

export type AgreementCultureParams = {
  range?: string | undefined;
  office?: string | undefined;
  compare?: boolean | undefined;
};

export async function fetchAgreementCultureSummary(
  offices: OfficeMapping[],
  params: AgreementCultureParams & { segment: SegmentFilter },
): Promise<AgreementCultureSummary> {
  if (mockFor("/agreement-culture")) {
    return mockResponse(generateAgreementCultureSummary(offices, params.segment, params.office));
  }
  return apiGet<AgreementCultureSummary>("/agreement-culture", {
    range: params.range,
    office: params.office,
    segment: params.segment,
    compare: params.compare ? "true" : undefined,
  });
}

export function useAgreementCultureSummary(
  segment: SegmentFilter,
  params: AgreementCultureParams = {},
) {
  const { data: offices, isError: officesFailed } = useOfficeMapping();

  return useQuery({
    queryKey: queryKey("agreement-culture-summary", {
      ...params,
      segment,
      offices: officesFingerprint(offices),
    }),
    queryFn: () => fetchAgreementCultureSummary(offices ?? [], { ...params, segment }),
    enabled: officesSettled(offices, officesFailed),
    staleTime: 60 * 1000,
  });
}
